import {
  ColumnDef,
  ColumnFiltersState,
  Row,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, MoreHorizontal } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { convertToEuro } from "@/libs/amount";
import { calculReporting } from "@/libs/utils";
import { Project } from "@/types";
import { useParams, useSearchParams } from "react-router-dom";

const TableItem = ({
  program,
  dataReport,
  rowSelections,
  setRowSelections,
  columnVisibility,
  setColumnVisibility,
  setProjects,
  financers,
  loadingFinancers,
}) => {
  const columns: ColumnDef<Project>[] = [
    {
      id: "select",
      header: ({ table }) => {
        const allPageRowIds = table
          .getRowModel()
          .rows.map((row) => row.original.id);
        const isAllSelected = allPageRowIds.every(
          (id) => Array.isArray(rowSelections) && rowSelections.includes(id)
        );
        const isSomeSelected = allPageRowIds.some(
          (id) => Array.isArray(rowSelections) && rowSelections.includes(id)
        );

        return (
          <Checkbox
            checked={
              isAllSelected ? true : isSomeSelected ? "indeterminate" : false
            }
            onCheckedChange={
              (value) =>
                setRowSelections((prev) => {
                  const updatedSelections = Array.isArray(prev)
                    ? [...prev]
                    : [];
                  return value
                    ? Array.from(
                        new Set([...updatedSelections, ...allPageRowIds])
                      )
                    : updatedSelections.filter(
                        (id) => !allPageRowIds.includes(id)
                      );
                })

              // setRowSelections((prev) => {
              //   const set = new Set(prev || []);
              //   if (value) {
              //     allPageRowIds.forEach((id) => set.add(id));
              //   } else {
              //     allPageRowIds.forEach((id) => set.delete(id));
              //   }
              //   return [...set];
              // })
            }
            aria-label="Select all"
          />
        );
      },
      cell: ({ row }) => (
        <Checkbox
          checked={
            Array.isArray(rowSelections) &&
            rowSelections.includes(row.original.id)
          }
          onCheckedChange={(value) =>
            setRowSelections((prev) => {
              const updatedSelections = Array.isArray(prev) ? [...prev] : [];
              return value
                ? [...updatedSelections, row.original.id] // Ajouter l'élément
                : updatedSelections.filter((id) => id !== row.original.id); // Supprimer l'élément
            })
          }
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "program",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Programmes
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem]"
            />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="text-balance">{row.getValue("program")}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: "project",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Projets
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem]"
            />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="text-balance">{row.getValue("project")}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: "status",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Statut
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem]"
            />
          </Button>
        );
      },
      cell: ({ row }) => {
        const val: string = row.getValue("status");

        const statusStyles = {
          "Pas encore commencé": { bg: "muted", text: "foreground" },
          "En cours": { bg: "secondary", text: "secondary-foreground" },
          "En attente de validation": { bg: "accent", text: "background" },
          Validé: { bg: "primary", text: "primary-foreground" },
          Rejeté: { bg: "destructive", text: "white" },
        };

        const { bg = "[#e7e7e7]", text = "foreground" } =
          statusStyles[val] || {};

        return (
          <div className="flex items-center gap-2 w-[167px]">
            <div
              className={`flex items-center text-xs px-3 py-[3px] h-[26px] rounded-full border gap-1 whitespace-nowrap bg-${bg} text-${text} border-0 inline-flex w-auto`}
            >
              {val}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "typology",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Typologie
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem]"
            />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="w-40 text-balance">{row.getValue("typology")}</div>
      ),
    },
    {
      accessorKey: "country",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Pays
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem]"
            />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("country")}</div>,
    },
    {
      accessorKey: "contributions",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 text-left cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Contributions déjà <br />
            obtenues
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem] ml-2"
            />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="w-8">{row.getValue("contributions")} €</div>
      ),
    },
    {
      accessorKey: "contributionCall",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 text-left cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Appel à <br /> contributions
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem] ml-2"
            />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("contributionCall")} €</div>,
    },
    {
      accessorKey: "projectOwner",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Porteur de projet
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem]"
            />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("projectOwner")}</div>,
    },
    {
      accessorKey: "launchDate",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Date de lancement
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem]"
            />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("launchDate")}</div>,
    },
    {
      accessorKey: "treesPlanted",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Arbres plantés
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem]"
            />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("treesPlanted")}</div>,
    },
    {
      accessorKey: "regeneratedArea",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Surfaces régénérées
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem]"
            />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("regeneratedArea")}</div>,
    },
    {
      accessorKey: "CO2Sequestre",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            CO2 Séquestrés
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem]"
            />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("CO2Sequestre")}</div>,
    },
    {
      accessorKey: "vegetationCover",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Couverture végétale
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem]"
            />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("vegetationCover")}</div>,
    },
    {
      accessorKey: "jobCreation",
      header: ({ column }) => {
        return (
          <Button
            variant="link"
            className="px-3 cursor-pointer text-foreground flex gap-2 font-normal"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Création d'emplois
            <ArrowUpDown
              size={13}
              className="text-gray-200 w-[.8rem] h-[.8rem]"
            />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("jobCreation")}</div>,
    },
  ];
  const [data, setData] = React.useState<Project[]>([]);

  React.useEffect(() => {
    (async () => {
      if (program.projects?.length) {
        const processedProjects = await Promise.all(
          program.projects.map(async (project) => {
            const totalDesiredFunding =
              Array.isArray(project?.desiredFunding) &&
              project.desiredFunding.length > 0
                ? (
                    await Promise.all(
                      project.desiredFunding.map((fund) =>
                        convertToEuro(fund.amount, fund.devise)
                      )
                    )
                  ).reduce((acc, value) => acc + value, 0)
                : 0;

            const totalObtainedFunding =
              Array.isArray(project?.obtainedFunding) &&
              project.obtainedFunding.length > 0
                ? (
                    await Promise.all(
                      project.obtainedFunding.map((fund) =>
                        convertToEuro(fund.amount, fund.devise)
                      )
                    )
                  ).reduce((acc, value) => acc + value, 0)
                : 0;

            const fees = totalDesiredFunding * 0.15;
            const callContributions = totalDesiredFunding + fees;

            let reporting = null;
            if (dataReport) {
              reporting = calculReporting(dataReport, null, [project]);
            }

            const indicatorMap =
              reporting?.projectTotalIndicators?.reduce((acc, pti) => {
                if (pti.type === 1) {
                  acc[pti.indicator?.code] = pti.total;
                }
                return acc;
              }, {}) || {};

            const statusMap = {
              0: "Pas encore commencé",
              1: "En cours",
              2: "En attente de validation",
              3: "Validé",
              4: "Rejeté",
            };
            return {
              id: project.id?.toString() || "",
              program: project.program.name,
              programId: project.program.id,
              project: project.name || "",
              status: statusMap[project.status] || "Inconnu",
              sold: project.soldStatus,
              typology: project.type?.name || "",
              country: project.country?.name || "",
              contributions: parseFloat(
                totalObtainedFunding.toFixed(2)
              ).toLocaleString("fr-FR"),

              contributionCall: parseFloat(
                callContributions.toFixed(2)
              ).toLocaleString("fr-FR"),
              projectOwner: `${
                project?.program?.carrier?.fullname || ""
              }`.trim(),
              isFunded: project.obtainedFunding?.length > 0 || false,
              launchDate: project.launchDate || "",
              treesPlanted:
                indicatorMap["arbres_plantes"]?.toLocaleString("fr-FR") || "0",
              regeneratedArea:
                indicatorMap["surface_regeneree"]?.toLocaleString("fr-FR") ||
                "0",
              CO2Sequestre:
                indicatorMap["tco2_sequestrees"]?.toLocaleString("fr-FR") ||
                "0",
              vegetationCover:
                indicatorMap["couverture_vegetale"]?.toLocaleString("fr-FR") ||
                "0",
              jobCreation:
                indicatorMap["creation_d_emploi"]?.toLocaleString("fr-FR") ||
                "0",
              financer: project.financer,
            };
          })
        );
        setData(processedProjects);
      }
    })();
  }, [program.projects, dataReport]);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [rowSelection, setRowSelection] = React.useState({});
  const translations = {
    status: "statut",
    sold: "Vendu ?",
    typology: "typologie",
    country: "pays",
    contributions: "Contributions déjà obtenu (FO)",
    contributionCall: "Appel à contributions",
    projectOwner: "Porteur de projet",
    isFunded: "Financé ?",
    launchDate: "Date de lancement",
    Indicateurs: "indicateurs",
    treesPlanted: "Arbres plantés",
    regeneratedArea: "Zones régénérées",
    CO2Sequestre: "CO2 séquestrés",
    vegetationCover: "Couverture végétale",
    jobCreation: "Création d'emplois",
  };
  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  React.useEffect(() => {
    const updatedSelections = { ...rowSelections };

    // Add or update selected rows
    Object.keys(rowSelection).forEach((key) => {
      if (rowSelection[key]) {
        updatedSelections[key] = rowSelection[key];
      }
    });

    // Remove unselected rows
    Object.keys(updatedSelections).forEach((key) => {
      if (!rowSelection[key]) {
        delete updatedSelections[key];
      }
    });

    setRowSelections(updatedSelections);
  }, [rowSelection]);

  React.useEffect(() => {
    setColumnVisibility(columnVisibility);
  }, [columnVisibility]);

  const TableRowMemo: React.FC<{ row: Row<any> }> = ({ row }) => {
    const [searchParams] = useSearchParams();
    const idProject = searchParams.get("idproject");
    const isHighlighted = idProject === row.original.id?.toString();
    const { firstname } = useParams();
    return (
      <TableRow
        key={row.id}
        data-state={row.getIsSelected() && "selected"}
        id={row.original.id}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} className="pr-0">
            {cell.column.id === "project" ? (
              <a
                href={`/${firstname}/project/${row.original.id}`}
                className="block w-36 text-balance"
              >
                {"" + cell.getValue()}
              </a>
            ) : cell.column.id === "program" ? (
              <a
                href={`/${firstname}/program/${row.original.programId}`}
                className="block w-36 text-balance"
              >
                {"" + cell.getValue()}
              </a>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const MemoizedRow = React.memo(TableRowMemo);

  return (
    <>
      <div className="w-full">
        <div className="rounded-md border relative">
          <div className="flex items-center py-1 absolute right-3 top-0 z-20  ">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="px-2 outline-none border-0 border-none ring-0 hover:bg-black/5"
                >
                  <MoreHorizontal className="text-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column, key) => {
                    return (
                      key <= 9 && (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          className="capitalize"
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                        >
                          {translations[column.id]}
                        </DropdownMenuCheckboxItem>
                      )
                    );
                  })}
                <DropdownMenuLabel className="mt-4">
                  Indicateurs
                </DropdownMenuLabel>
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column, key) => {
                    return (
                      key > 9 && (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          className="capitalize"
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                        >
                          {translations[column.id]}
                        </DropdownMenuCheckboxItem>
                      )
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="pr-[60px]">
            <ScrollArea className="w-full">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id} className="hover:bg-white">
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead
                            key={header.id}
                            className={`px-0 ${
                              header.id == "select" ? "text-center" : ""
                            }`}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table
                      .getRowModel()
                      .rows.map((row, key) => (
                        <MemoizedRow row={row} key={key} />
                      ))
                  ) : (
                    <TableRow className="hover:bg-white">
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        Pas de resultats.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        </div>
      </div>
    </>
  );
};

export default TableItem;
